import { useEffect, useState } from "react";
import { useParams, Link, Navigate, useNavigate } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";

const ALIASES = {
  "tree-services": "tree-service",
  handyman: "construction-handyman",
  construction: "construction-handyman",
  "home-repairs": "construction-handyman",
  cleaning: "cleaning-services",
  "lawn-care": "landscaping",
};

const BLURBS = {
  "tree-service": "From storm-damaged tree removal to trimming and stump grinding, local tree professionals help protect your property year-round.",
  "construction-handyman": "General construction, handyman work and home repairs — from small fixes to larger building projects handled by local pros.",
  landscaping: "Lawn care, garden design, sod, shrubs and full outdoor makeovers from landscapers who know North Alabama yards.",
  "cleaning-services": "Residential and commercial cleaning services, from deep cleans to recurring maintenance.",
  painting: "Interior and exterior painting with proper prep, quality finishes and clean job sites.",
  roofing: "Roof repairs, replacements and inspections from local roofing contractors.",
  flooring: "Hardwood, laminate, tile and vinyl flooring installation and repair.",
  plumbing: "Leak repairs, water heaters, drain cleaning and full plumbing service — including emergencies.",
  remodeling: "Kitchen, bathroom and whole-home remodeling projects managed by experienced local contractors.",
  "pressure-washing": "Driveways, siding, decks and patios restored with professional pressure washing.",
  electrical: "Licensed electrical work: panels, wiring, fixtures, outlets and emergency electrical issues.",
  "other-services": "Need something else? Post your project and we'll match you with the right local professional.",
};

const Shell = ({ children, seo }) => (
  <div className="min-h-screen bg-white">
    {seo}
    <header className="bg-navy">
      <div className="max-w-4xl mx-auto px-6 h-16 flex items-center">
        <Link to="/" data-testid="slp-logo-link" className="flex items-center gap-3">
          <span className="w-8 h-8 bg-flame flex items-center justify-center font-heading font-black text-white text-lg">P</span>
          <span className="font-heading font-black text-white tracking-tight">PRIME PRO</span>
        </Link>
      </div>
    </header>
    <main className="max-w-4xl mx-auto px-6 py-16">{children}</main>
    <footer className="bg-navy py-6 mt-10">
      <div className="max-w-4xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-2">
        <p className="text-white/40 text-xs">© 2026 Prime Pro Connect Services</p>
        <Link to="/privacy-policy" className="text-white/50 hover:text-flame text-xs transition-colors">Privacy Policy</Link>
      </div>
    </footer>
  </div>
);

const CtaButton = () => {
  const navigate = useNavigate();
  return (
    <button data-testid="slp-cta-btn" onClick={() => navigate("/request/new")}
      className="mt-10 inline-flex items-center gap-3 bg-flame hover:bg-flame-dark text-white font-heading font-bold uppercase tracking-wider text-sm px-8 py-4 transition-transform hover:scale-[1.03]">
      Request a Free Estimate <ArrowRight size={16} />
    </button>
  );
};

export default function ServiceLocationPage() {
  const { slug } = useParams();
  const [categories, setCategories] = useState(null);
  const [locations, setLocations] = useState(null);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => setCategories([]));
    api.get("/locations").then((r) => setLocations(r.data)).catch(() => setLocations([]));
  }, []);

  if (categories === null || locations === null) {
    return <div className="min-h-screen bg-white flex items-center justify-center">
      <span className="inline-block w-8 h-8 border-2 border-flame border-t-transparent rounded-full animate-spin" /></div>;
  }

  const catSlug = ALIASES[slug] || slug;
  const category = categories.find((c) => c.slug === catSlug);

  if (category) {
    const title = `${category.name} in Huntsville & North Alabama | Prime Pro Connect Services`;
    const desc = `Request free ${category.name.toLowerCase()} estimates from trusted local professionals in Huntsville, Madison, Decatur, Athens and surrounding Alabama areas.`;
    return (
      <Shell seo={<Seo title={title} description={desc} path={`/${slug}`} />}>
        <p className="uppercase tracking-[0.25em] text-flame font-heading font-bold text-sm mb-3">Local Service</p>
        <h1 data-testid="service-page-heading" className="font-heading font-black tracking-tighter text-navy text-4xl sm:text-5xl">
          {category.name} in Huntsville &amp; North Alabama
        </h1>
        <p className="text-slate-600 text-base leading-relaxed mt-6 max-w-2xl">{BLURBS[catSlug] || BLURBS["other-services"]}</p>
        <p className="text-slate-600 text-base leading-relaxed mt-4 max-w-2xl">
          Prime Pro Connect Services matches your request with local {category.name.toLowerCase()} professionals
          serving Huntsville, Madison, Decatur, Athens, Hazel Green, Cullman and surrounding
          North Alabama communities — with expansion into Tennessee. Post your project once,
          compare offers, and choose the pro that fits your budget.
        </p>
        <CtaButton />
        <h2 className="font-heading font-black tracking-tight text-navy text-lg mt-14 mb-4">Areas we serve</h2>
        <div className="flex flex-wrap gap-2">
          {locations.map((l) => (
            <Link key={l.id} to={`/${l.slug}-al`} data-testid={`service-city-${l.slug}`}
              className="flex items-center gap-2 border border-border px-4 py-2 text-sm text-navy hover:border-flame transition-colors">
              <MapPin size={13} className="text-flame" /> {l.city}, AL
            </Link>
          ))}
        </div>
      </Shell>
    );
  }

  const locSlug = slug.endsWith("-al") ? slug.slice(0, -3) : null;
  const location = locSlug ? locations.find((l) => l.slug === locSlug) : null;

  if (location) {
    const title = `Local Service Professionals in ${location.city}, Alabama | Prime Pro Connect Services`;
    const desc = `Find trusted local pros in ${location.city}, AL for tree service, roofing, plumbing, landscaping, cleaning, remodeling and more. Request a free estimate.`;
    return (
      <Shell seo={<Seo title={title} description={desc} path={`/${slug}`} />}>
        <p className="uppercase tracking-[0.25em] text-flame font-heading font-bold text-sm mb-3">Service Area</p>
        <h1 data-testid="location-page-heading" className="font-heading font-black tracking-tighter text-navy text-4xl sm:text-5xl">
          Local Service Professionals in {location.city}, Alabama
        </h1>
        <p className="text-slate-600 text-base leading-relaxed mt-6 max-w-2xl">
          Prime Pro Connect Services helps homeowners and businesses in {location.city} find
          trusted local professionals. Describe your project — including urgent and emergency
          work — and matching pros serving {location.city} respond with offers. No fake reviews,
          no inflated numbers: just real local providers.
        </p>
        <CtaButton />
        <h2 className="font-heading font-black tracking-tight text-navy text-lg mt-14 mb-4">Popular services in {location.city}</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {categories.map((c) => (
            <Link key={c.id} to={`/${c.slug === "tree-service" ? "tree-services" : c.slug}`} data-testid={`location-service-${c.slug}`}
              className="border border-border px-4 py-3 text-sm text-navy hover:border-flame transition-colors">
              {c.name}
            </Link>
          ))}
        </div>
      </Shell>
    );
  }

  return <Navigate to="/" replace />;
}
